import { t } from "ttag";

import {
  getPaymentStatusLabel,
  PAYMENT_STATUS_COLOR,
} from "@/features/billing/models";
import { PaymentMethodDisplay } from "@/features/payments/components";
import type { Invoice } from "@/shared/domains";
import {
  IS_WEB_VIEW,
  sendMessageToReactNativeWebView,
  WEB_VIEW_ACTIONS,
} from "@/shared/lib";
import { Badge, EmptyState, LinkMinimalistic, TableCard } from "@/shared/ui";
import { formatISO } from "@/shared/utils";

interface PaymentHistoryCardsProps {
  payments: Invoice[];
}

export const PaymentHistoryCards = ({ payments }: PaymentHistoryCardsProps) => {
  if (!payments.length) {
    return (
      <EmptyState.Root className="md:hidden">
        <EmptyState.Icon />
        <EmptyState.Description>
          {t`You haven't had any payment yet`}
        </EmptyState.Description>
      </EmptyState.Root>
    );
  }

  return (
    <TableCard.Root>
      {payments.map(
        ({
          id,
          status,
          amount,
          hostedInvoiceUrl,
          date,
          productCategory,
          detail,
          paymentMethod,
        }) => (
          <TableCard.Row key={`past-payment-${id}`}>
            <TableCard.Cell>
              <TableCard.Data>{detail}</TableCard.Data>
            </TableCard.Cell>

            <TableCard.Cell>
              <TableCard.Label>{t`Date`}</TableCard.Label>
              <TableCard.Data>{formatISO(date)}</TableCard.Data>
            </TableCard.Cell>

            <TableCard.Cell>
              <TableCard.Label>{t`Plan`}</TableCard.Label>
              <TableCard.Data>{productCategory?.name ?? "-"}</TableCard.Data>
            </TableCard.Cell>

            <TableCard.Cell>
              <TableCard.Label>{t`Amount`}</TableCard.Label>
              <TableCard.Data>${amount}</TableCard.Data>
            </TableCard.Cell>

            <TableCard.Cell>
              <TableCard.Label>{t`Payment`}</TableCard.Label>
              <TableCard.Data>
                <PaymentMethodDisplay paymentMethod={paymentMethod} compact />
              </TableCard.Data>
            </TableCard.Cell>

            <TableCard.Cell>
              <Badge color={PAYMENT_STATUS_COLOR[status]} className="min-w-24">
                {getPaymentStatusLabel(status)}
              </Badge>

              {hostedInvoiceUrl ? (
                <a
                  href={hostedInvoiceUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-0.5"
                  onClick={(e) => {
                    e.preventDefault();
                    if (IS_WEB_VIEW) {
                      sendMessageToReactNativeWebView({
                        type: WEB_VIEW_ACTIONS.GO_TO_EXTERNAL_URL,
                        payload: { url: hostedInvoiceUrl },
                      });
                    } else {
                      window.open(hostedInvoiceUrl, "_blank");
                    }
                  }}
                >
                  <LinkMinimalistic className="size-4" />
                  {t`Invoice`}
                </a>
              ) : (
                <TableCard.Data>-</TableCard.Data>
              )}
            </TableCard.Cell>
          </TableCard.Row>
        ),
      )}
    </TableCard.Root>
  );
};
